<template>
  <div
    v-if="[THEMES.RDK, THEMES.DJURENS_RATT].includes(theme)"
    class="relative mx-auto max-w-[1920px] lg:grid lg:grid-cols-2 lg:gap-6 lg:px-6"
  >
    <div
      v-if="image"
      class="relative aspect-[375/230] w-full overflow-hidden rounded-t-2xl md:aspect-auto md:h-[295px] lg:aspect-[912/752] lg:h-full lg:rounded-2xl xl:sticky xl:top-4 xl:order-last"
    >
      <Image
        :url="image"
        :image-class="imageVariants({ theme })"
        :fetchpriority="criticalImage ? 'high' : 'auto'"
        :loading="criticalImage ? 'eager' : 'lazy'"
      />
    </div>
    <div :class="blockVariants({ theme }) + ' pb-2 pt-6'">
      <div class="flex w-full flex-col items-center justify-center md:!px-9">
        <div class="w-full">
          <Title v-if="title" :class="titleVariants({ theme })" :level="2">
            {{ title }}
          </Title>
          <h3 v-if="subTitle" :class="subTitleVariants({ theme })">{{ subTitle }}</h3>
        </div>

        <slot></slot>
      </div>
    </div>
  </div>
  <div v-else class="relative mx-auto max-w-[1920px]">
    <Container class="relative">
      <div class="mb-4 lg:max-w-[650px]">
        <Title v-if="title" custom-style="mb-4 lg:mb-10 lg:text-4xl" :level="2">
          {{ title }}
        </Title>
        <div
          :class="!hideBackground && 'rounded-xl bg-blue-lighter px-6 py-4 lg:rounded-2xl lg:p-9'"
        >
          <slot></slot>
        </div>
      </div>
      <Image
        v-if="image && tallImage"
        class="!absolute right-0 top-0 hidden h-full max-h-[881px] w-full lg:block lg:max-w-[45%] lg:rounded-2xl"
        :url="image"
        :fetchpriority="criticalImage ? 'high' : 'auto'"
        :loading="criticalImage ? 'eager' : 'lazy'"
      />
    </Container>
    <Image
      v-if="image && !tallImage"
      class="!absolute right-0 top-0 hidden h-full max-h-[700px] w-full lg:block lg:max-w-[45%] lg:rounded-l-2xl"
      :url="image"
      :fetchpriority="criticalImage ? 'high' : 'auto'"
      :loading="criticalImage ? 'eager' : 'lazy'"
    />
  </div>
</template>

<script setup lang="ts">
import { Container, Title, THEMES } from 'refresh-ui'
import Image from '../atoms/Image.vue'

import { cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

const props = withDefaults(
  defineProps<{
    title?: string
    subTitle?: string
    image?: string
    tallImage?: boolean
    theme?: THEMES
    hideBackground?: boolean
    criticalImage?: boolean
  }>(),
  {
    tallImage: false,
    theme: THEMES.DJURENS_RATT,
    hideBackground: false,
    criticalImage: false,
  },
)
const blockVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'relative z-10 -mt-4 flex aspect-square w-full flex-col items-center rounded-t-2xl bg-rdk px-4 py-6 lg:mt-0 lg:rounded-2xl lg:py-12 lg:px-0 justify-center',
      [THEMES.DJURENS_RATT]: twMerge(
        'relative z-10 -mt-4 flex w-full flex-col items-center rounded-2xl bg-blue-lighter px-4 py-6 lg:mt-0 lg:rounded-2xl lg:py-12 lg:px-0 lg:justify-center',
        props.image ? 'aspect-square' : 'pt-12',
      ),
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const imageVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]: 'h-full max-h-[400px] xl:max-h-[600px] w-full object-cover lg:rounded-2xl',
      [THEMES.DJURENS_RATT]:
        'h-full max-h-[645px] w-full object-cover lg:rounded-2xl xl:max-h-[924px]',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const titleVariants = cva('mb-4', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'w-full font-normal font-headline text-purple-act-dark text-h2 lg:text-h2 lg:text-center',
      [THEMES.DJURENS_RATT]: 'w-full text-black lg:text-4xl lg:text-center mb-0',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const subTitleVariants = cva('text-[20px] lg:text-2xl', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'mb-6 text-black',
      [THEMES.RDK]: 'lg:text-center font-avenir text-purple-act-dark mb-9 lg:mb-10 text-h3',
      [THEMES.DJURENS_RATT]: 'text-black lg:text-center mb-9 lg:mb-10',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
